import { z } from "zod";

const hostnameRe = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

/** Kullanıcının girdiği mağaza adresini https://alan-adi biçimine indirger. */
export const storeUrlSchema = z
  .string({ error: "Mağaza adresi gerekli" })
  .trim()
  .min(4, { error: "Mağaza adresi gerekli" })
  .max(300)
  .transform((raw, ctx) => {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    let url: URL;
    try {
      url = new URL(withScheme);
    } catch {
      ctx.addIssue({ code: "custom", message: "Geçerli bir mağaza adresi girin" });
      return z.NEVER;
    }
    if (url.protocol !== "https:") {
      ctx.addIssue({ code: "custom", message: "Mağaza adresi https olmalı" });
      return z.NEVER;
    }
    if (url.username || url.password || url.port) {
      ctx.addIssue({ code: "custom", message: "Adreste kullanıcı adı, şifre veya port olamaz" });
      return z.NEVER;
    }
    if (!hostnameRe.test(url.hostname)) {
      ctx.addIssue({ code: "custom", message: "Geçerli bir alan adı girin (ör. magazaniz.com)" });
      return z.NEVER;
    }
    return `https://${url.hostname.toLowerCase()}`;
  });

export const ideasoftConnectSchema = z.object({
  storeUrl: storeUrlSchema,
  clientId: z.string().trim().min(3, { error: "İstemci kimliği gerekli" }).max(200),
  clientSecret: z.string().trim().min(8, { error: "İstemci gizli anahtarı gerekli" }).max(400),
});
export type IdeasoftConnectInput = z.input<typeof ideasoftConnectSchema>;

export const trendyolConnectSchema = z.object({
  sellerId: z.string().trim().regex(/^\d{3,12}$/, { error: "Satıcı ID yalnızca rakamlardan oluşmalı" }),
  apiKey: z.string().trim().min(8, { error: "API Key gerekli" }).max(200),
  apiSecret: z.string().trim().min(8, { error: "API Secret gerekli" }).max(200),
  environment: z.enum(["prod", "stage"]),
  userAgentMode: z.enum(["self", "integrator"]),
});
export type TrendyolConnectInput = z.input<typeof trendyolConnectSchema>;

/**
 * Hepsiburada: Mağaza ID (GUID), satıcı panelindeki Entegrasyon → Entegratör Bilgileri'nden alınan
 * servis anahtarı ve entegratör kullanıcı adı (User-Agent). Test ortamı (SIT) ayrı bilgilerle çalışır.
 */
export const hepsiburadaConnectSchema = z.object({
  merchantId: z
    .string()
    .trim()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, { error: "Mağaza ID biçimi: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" }),
  serviceKey: z.string().trim().min(8, { error: "Servis anahtarı gerekli" }).max(400),
  integratorName: z.string().trim().regex(/^[A-Za-z0-9_.-]{2,60}$/, { error: "Entegratör kullanıcı adı: harf, rakam, _ . -" }),
  environment: z.enum(["prod", "sit"]),
});
export type HepsiburadaConnectInput = z.input<typeof hepsiburadaConnectSchema>;

/**
 * WooCommerce (WordPress): site adresi (alt klasörde kuruluysa klasörüyle) ve WooCommerce → Ayarlar →
 * Gelişmiş → REST API'den "Okuma/Yazma" yetkisiyle oluşturulan tüketici anahtarı (ck_) ve gizli anahtarı (cs_).
 */
export const woocommerceConnectSchema = z.object({
  storeUrl: z
    .string({ error: "Site adresi gerekli" })
    .trim()
    .min(4, { error: "Site adresi gerekli" })
    .max(300)
    .transform((raw, ctx) => {
      let url: URL;
      try {
        url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
      } catch {
        ctx.addIssue({ code: "custom", message: "Geçerli bir site adresi girin" });
        return z.NEVER;
      }
      if (url.protocol !== "https:") {
        ctx.addIssue({ code: "custom", message: "Site adresi https olmalı (WooCommerce anahtarları yalnız https üzerinden gönderilir)" });
        return z.NEVER;
      }
      if (url.username || url.password || url.port) {
        ctx.addIssue({ code: "custom", message: "Adreste kullanıcı adı, şifre veya port olamaz" });
        return z.NEVER;
      }
      if (!hostnameRe.test(url.hostname)) {
        ctx.addIssue({ code: "custom", message: "Geçerli bir alan adı girin (ör. magazaniz.com)" });
        return z.NEVER;
      }
      // WordPress alt klasöre kuruluysa klasör korunur; wp-admin / wp-json gibi ekler atılır.
      const path = url.pathname.replace(/\/(wp-admin|wp-json|wp-login\.php)(\/.*)?$/i, "").replace(/\/+$/, "");
      return `https://${url.hostname.toLowerCase()}${path}`;
    }),
  consumerKey: z.string().trim().regex(/^ck_[A-Za-z0-9]{20,64}$/, { error: "Tüketici anahtarı ck_ ile başlar" }),
  consumerSecret: z.string().trim().regex(/^cs_[A-Za-z0-9]{20,64}$/, { error: "Gizli anahtar cs_ ile başlar" }),
});
export type WoocommerceConnectInput = z.input<typeof woocommerceConnectSchema>;

/** Ticimax: mağaza adresi ve Ticimax paneli → Ayarlar → WS Yetki Kodu Yönetimi'nde oluşturulan yetki kodu. */
export const ticimaxConnectSchema = z.object({
  storeUrl: storeUrlSchema,
  authCode: z.string().trim().min(8, { error: "Yetki kodu gerekli" }).max(200).regex(/^\S+$/, { error: "Yetki kodunda boşluk olamaz" }),
});
export type TicimaxConnectInput = z.input<typeof ticimaxConnectSchema>;

/**
 * Amazon (Selling Partner API): Satıcı ID (Merchant Token) ve Seller Central → Uygulamalar ve Hizmetler →
 * Uygulama geliştirme'de oluşturulan özel uygulamanın LWA kimlik bilgileri ile kendi yetkilendirmeden alınan
 * yenileme jetonu. Pazar yeri Türkiye.
 */
export const amazonConnectSchema = z.object({
  sellerId: z.string().trim().regex(/^A[0-9A-Z]{8,20}$/, { error: "Satıcı ID A ile başlar (ör. A1B2C3D4E5F6G7)" }),
  clientId: z.string().trim().regex(/^amzn1\.application-oa2-client\.[0-9a-f]{20,64}$/i, { error: "İstemci kimliği amzn1.application-oa2-client. ile başlar" }),
  clientSecret: z.string().trim().min(20, { error: "İstemci gizli anahtarı gerekli" }).max(400),
  refreshToken: z.string().trim().regex(/^Atzr\|\S{20,}$/, { error: "Yenileme jetonu Atzr| ile başlar" }),
  environment: z.enum(["prod", "sandbox"]),
});
export type AmazonConnectInput = z.input<typeof amazonConnectSchema>;

/** Pazarama: İş Ortağım paneli → Hesap Bilgileri → Entegrasyon Bilgileri'ndeki istemci kimliği ve gizli anahtar (API Key / Secret). */
export const pazaramaConnectSchema = z.object({
  clientId: z.string().trim().min(6, { error: "İstemci kimliği (API Key) gerekli" }).max(200).regex(/^\S+$/, { error: "Boşluk olamaz" }),
  clientSecret: z.string().trim().min(12, { error: "Gizli anahtar (API Secret) gerekli" }).max(400),
});
export type PazaramaConnectInput = z.input<typeof pazaramaConnectSchema>;

/**
 * Shopify (GraphQL Admin API): mağaza (xxx.myshopify.com; özel alan adı değil) ve Admin API erişimi. İki yol:
 * Dev Dashboard uygulamasının istemci kimliği + gizli anahtarı (client credentials, jeton 24 saat; kendi organizasyonunuzdaki
 * mağaza) ya da eski yönetimden oluşturulmuş özel uygulamanın Admin API erişim jetonu (shpat_).
 */
const SHOP_HANDLE = /^[a-z0-9][a-z0-9-]{0,58}[a-z0-9]$/;
export const shopifyShopSchema = z
  .string({ error: "Mağaza adresi gerekli" })
  .trim()
  .min(2, { error: "Mağaza adresi gerekli" })
  .max(300)
  .transform((raw, ctx) => {
    let v = raw.toLowerCase().replace(/^https?:\/\//, "");
    const admin = /^admin\.shopify\.com\/store\/([a-z0-9-]+)/.exec(v);
    if (admin) v = admin[1]!;
    v = v.split(/[/?#]/)[0]!.replace(/\.myshopify\.com$/, "");
    if (!SHOP_HANDLE.test(v)) {
      ctx.addIssue({ code: "custom", message: "Mağaza adı: xxx.myshopify.com adresindeki xxx (özel alan adı değil)" });
      return z.NEVER;
    }
    return `${v}.myshopify.com`;
  });
export const shopifyConnectSchema = z
  .object({
    shop: shopifyShopSchema,
    accessToken: z.string().trim().max(400).optional(),
    clientId: z.string().trim().max(200).optional(),
    clientSecret: z.string().trim().max(400).optional(),
  })
  .superRefine((v, ctx) => {
    const token = Boolean(v.accessToken);
    const pair = Boolean(v.clientId || v.clientSecret);
    if (token && pair) ctx.addIssue({ code: "custom", path: ["accessToken"], message: "Ya erişim jetonu ya da istemci kimliği + gizli anahtar girin; ikisi birden olmaz" });
    else if (token) {
      if (!/^shp(at|ca|ua)_[A-Za-z0-9]{16,}$/.test(v.accessToken!)) ctx.addIssue({ code: "custom", path: ["accessToken"], message: "Erişim jetonu shpat_ ile başlar" });
    } else if (pair) {
      if (!v.clientId || v.clientId.length < 8 || /\s/.test(v.clientId)) ctx.addIssue({ code: "custom", path: ["clientId"], message: "İstemci kimliği gerekli" });
      if (!v.clientSecret || v.clientSecret.length < 8) ctx.addIssue({ code: "custom", path: ["clientSecret"], message: "Gizli anahtar gerekli" });
    } else ctx.addIssue({ code: "custom", path: ["accessToken"], message: "Erişim jetonu ya da istemci kimliği + gizli anahtar girin" });
  });
export type ShopifyConnectInput = z.input<typeof shopifyConnectSchema>;

export const demoConnectSchema = z.object({ channel: z.enum(["ideasoft", "trendyol"]) });
